import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CommonDataService } from 'src/app/shared/services/common-data.service';
import * as moment from 'moment';
import { DeviceFactory } from './device-factory/device-factory';
import { DomainsService } from 'src/app/shared/services/domains.service';
import { shiftScheduleOptions } from '../site-settings/site-utilization-settings/site-utilization-settings.interface';


@Injectable({
	providedIn: 'root'
})

export class DeviceCommonService {

	booleanFields: string[] = [ 'in_test_mode', 'setup_done', 'enable_current_mask',
								'disable_xtra', 'disable_external_rtc', 'has_current_low_range',
								'has_temperature_sensor', 'has_water_level', 'enable_rt',
								'enable_multicast', 'enable_wifi_ap_on_start', 'enable_serversocket', 'is_online_enabled',
								'enable_cellular', 'enable_wifi_sta', 'cal_status', 'replacement_part'];

	customFields: string[] = ['battery_type', 'cellular_region', 'metered_flag', 'zone_id'];
	nbIotFields: string[] = ['lte_cellular_bands', 'nbiot_cellular_bands'];
	timeStampFields: string[] = ['installation_date', 'setup_time'];
	networkingFields: string[] = ['mobile_master', 'mobile_network', 'router_network', 'online_network'];

	ddLists = {
		current_cable_scale: [
			{id: 1, label: this.translate.instant('studies.single')},
			{id: 2, label: this.translate.instant('studies.dual')},
		],
		dead_man_current_limit: [],
		battery_type: [
			{id: 0, label: this.translate.instant('events.battery_type_0')},
			{id: 2, label: this.translate.instant('events.battery_type_2')},
		],
		number_of_cells: [
			{id: 4, label: 4},
			{id: 8, label: 8},
			{id: 12, label: 12},
			{id: 16, label: 16},
			{id: 18, label: 18},
			{id: 24, label: 24},
			{id: 28, label: 28},
			{id: 36, label: 36},
			{id: 40, label: 40},
		],
		remoter_server_ip: [
			{id: 'production',	text: this.translate.instant('env.production'),	port: 9311, encCode: 2, domain: this.domainsService.iotahProductionDomain},
			{id: 'staging',		text: this.translate.instant('env.staging'), 	port: 9359, encCode: 2, domain: this.domainsService.iotahStagingDomain},
			{id: 'development',	text: this.translate.instant('env.dev'), 		port: 9311, encCode: 1, domain: this.domainsService.iotahDevelopmentDomain},
			{id: 'custom',		text: this.translate.instant('env.custom'), 	port: 9311, encCode: 1, domain: ''}
		],

		im_remoter_server_ip: [
			{id: 'production',	text: this.translate.instant('env.production'),	port: 9313, encCode: 2, domain: this.domainsService.imProductionDomain},
			{id: 'staging',		text: this.translate.instant('env.staging'), 	port: 9314, encCode: 2, domain: this.domainsService.imStagingDomain},
			{id: 'development',	text: this.translate.instant('env.dev'), 		port: 9313, encCode: 1, domain: this.domainsService.imDevelopmentDomain},
			{id: 'custom',		text: this.translate.instant('env.custom'), 	port: 9313, encCode: 1, domain: ''}
		],

		cellular_region: [
			{ text: this.translate.instant('g.usa_and_canada'), id: 0 },
			{ text: this.translate.instant('g.usa'), id: 1 },
			{ text: this.translate.instant('g.canada'), id: 2 },
		],

		charger_voltage: [ 
			{ text: '24', value: 24 },
			{ text: '36', value: 36 },
			{ text: '48', value: 48 },
			{ text: '80', value: 80 },
			{ text: this.translate.instant('devices.multi_voltage'), value: 999 },
		], 

		rt_log_frequency_units: [
			{ text: this.translate.instant('g.seconds'), value: 0 },
			{ text: this.translate.instant('g.minutes'), value: 1 },
		],

		lte_bands: [ // 0x8fe
			{ text: "BAND B1", value: 0x01 },
			{ text: "BAND B2", value: 0x02 }, //
			{ text: "BAND B3", value: 0x04 }, //
			{ text: "BAND B4", value: 0x08 }, //
			{ text: "BAND B5", value: 0x10 }, //
			{ text: "BAND B8", value: 0x20 }, //
			{ text: "BAND B12", value: 0x40 }, //
			{ text: "BAND B13", value: 0x80 }, //
			{ text: "BAND B18", value: 0x100 },
			{ text: "BAND B19", value: 0x200 },
			{ text: "BAND B20", value: 0x400 },
			{ text: "BAND B25", value: 0x800 }, //
			{ text: "BAND B26", value: 0x1000 },
			{ text: "BAND B27", value: 0x2000 },
			{ text: "BAND B28", value: 0x4000 },
			{ text: "BAND B66", value: 0x10000 },
			{ text: "BAND B85", value: 0x80000 },
		],

		nbiot_bands: [
			{ text: "BAND B1", value: 0x01 },
			{ text: "BAND B2", value: 0x02 },
			{ text: "BAND B3", value: 0x04 },
			{ text: "BAND B4", value: 0x08 },
			{ text: "BAND B5", value: 0x10 },
			{ text: "BAND B8", value: 0x20 },
			{ text: "BAND B12", value: 0x40 },
			{ text: "BAND B13", value: 0x80 },
			{ text: "BAND B18", value: 0x100 },
			{ text: "BAND B19", value: 0x200 },
			{ text: "BAND B20", value: 0x400 },
			{ text: "BAND B25", value: 0x800 },
			{ text: "BAND B28", value: 0x4000 },
			{ text: "BAND B66", value: 0x10000 },
			{ text: "BAND B71", value: 0x100000 },
			{ text: "BAND B85", value: 0x80000 },
		],

		metered_flag: [
			{ label: this.translate.instant('devices.cellular_only'), value: 1 },
			{ label: this.translate.instant('devices.metered_flag_both'), value: 2 },
			{ label: this.translate.instant('devices.metered_flag_none'), value: 3 },
		],

		scheduledOption: [
			{ label: this.translate.instant('site_utilization_settings.single_shift'), value: shiftScheduleOptions.SINGLE_SHIFT },
			{ label: this.translate.instant('site_utilization_settings.two_shifts'), value: shiftScheduleOptions.TWO_SHIFTS },
			{ label: this.translate.instant('site_utilization_settings.three_shifts'), value: shiftScheduleOptions.THREE_SHIFTS },
		],

		networkPriorities: [
			{id: 0, text: this.translate.instant('priority_options.0')},
			{id: 1, text: this.translate.instant('priority_options.1')},
			{id: 2, text: this.translate.instant('priority_options.2')},
			{id: 3, text: this.translate.instant('priority_options.3')},
			{id: 10, text: this.translate.instant('priority_options.10')}
		],

		ctRatioOptions: [
			{ id: 50, text: '50A/0.333V' },
			{ id: 100, text: '100A/0.333V' },
			{ id: 150, text: '150A/0.333V' },
			{ id: 200, text: '200A/0.333V' },
			{ id: 400, text: '400A/0.333V' },
			{ id: 600, text: '600A/0.333V' },
		],
		ctRatioRogowskiType: [
			{ id: 1000, text: '1000A' },
			{ id: 2500, text: '2500A' },
		],

		serviceTypes: [
			{ id: 1, text: '4-Wire Wye' }, // --> VCONSEL :0, ICONSEL:0
			{ id: 2, text: '3-Wire Delta' }, //-->  VCONSEL :1, ICONSEL:1"
		],

		wiringModeSettings: {
			0: {
				value: 0,
				name: '3_phase_4_wire',
				text: this.translate.instant('settings_device_settings.3_phase_4_wire'),
				enabledWiresOptions: [
					{ value: 0x07, text: `${this.translate.instant('quick_view.phase_a')} / ${this.translate.instant('quick_view.phase_b')} / ${this.translate.instant('quick_view.phase_c')}` },
				],
				enabledWiresDefault: 0x07,
			},
			1: {
				value: 1,
				name: '3_phase_3_wire',
				text: this.translate.instant('settings_device_settings.3_phase_3_wire'),
				enabledWiresOptions: [
					{ value: 0x07, text: `${this.translate.instant('quick_view.phase_a')} / ${this.translate.instant('quick_view.phase_b')} / ${this.translate.instant('quick_view.phase_c')}` },
				],
				enabledWiresDefault: 0x07,
				voltageReferenceDefault: 'LINE_TO_LINE',
			},
			2: {
				value: 2,
				name: 'single_phase_3_wire',
				text: this.translate.instant('settings_device_settings.single_phase_3_wire'),
				enabledWiresOptions: [
					{ value: 0x03, text: `${this.translate.instant('quick_view.phase_a')} / ${this.translate.instant('quick_view.phase_b')}` },
					{ value: 0x05, text: `${this.translate.instant('quick_view.phase_a')} / ${this.translate.instant('quick_view.phase_c')}` },
					{ value: 0x06, text: `${this.translate.instant('quick_view.phase_b')} / ${this.translate.instant('quick_view.phase_c')}` }
				],
				enabledWiresDefault: 0x03,
				wiringModeNote: this.translate.instant('settings_device_settings.connect_single_phase_3_wire_note')
			},
			3: {
				value: 3,
				name: 'single_phase_2_wire',
				text: this.translate.instant('settings_device_settings.single_phase_2_wire'),
				enabledWiresOptions: [
					{ value: 0x01, text: this.translate.instant('quick_view.phase_a') },
					{ value: 0x02, text: this.translate.instant('quick_view.phase_b') },
					{ value: 0x04, text: this.translate.instant('quick_view.phase_c') }
				],
				enabledWiresDefault: 0x01,
				voltageReferenceDefault: 'LINE_TO_NEUTRAL',
				wiringModeNote: this.translate.instant('settings_device_settings.connect_single_phase_2_wire_note')
			},
		},

		voltageReferenceOptions: {
			LINE_TO_LINE: { value: 0, text: this.translate.instant('settings_device_settings.line_to_line') },
			LINE_TO_NEUTRAL: { value: 1, text: this.translate.instant('settings_device_settings.line_to_neutral') },
		},

		hallEffectSensorTypes: [
			{ id: 0, label: this.translate.instant('devices.dual_screw'), minFwVersion: 1.1000 },
			{ id: 1, label: this.translate.instant('devices.single_latch'), minFwVersion: 2.4650 },
			{ id: 2, label: this.translate.instant('devices.single_latch_250'), minFwVersion: 2.5200 },
			{ id: 3, label: this.translate.instant('devices.dual_latch_750'), minFwVersion: 2.5200 },
		],
	};

	chargersType: Array<{ label: string, value: number }> = [
		{ label: this.translate.instant('devices.conventional_charge'), value: 0},
		{ label: this.translate.instant('devices.fast_charger'), value: 1},
		{ label: this.translate.instant('devices.opportunity_charge'), value: 2},
	];

	deviceSettingTabsTranslation = {
		'info': 'devices.device_info',
		'deviceSettings': 'g.device_settings',
		'batterySettings': 'g.battery_settings',
		'cal': 'g.calibration',
		'networkingSettings': 'g.networking_settings',
		'networking': 'g.networking',
		'billedKWHrsSettings': 'g.billed_kwhrs_settings',
		'maintenanceSchedule': 'g.maintenance_schedule',
		'official-reporting': 'g.official_reporting',
		'connectivityFrequency': 'g.connectivity_frequency',
		'loadScheduling': 'iotemeter.g.load_scheduling',
		'eventTypeSettings': 'g.event_type_settings'
	};

	constructor(
		private translate: TranslateService,
		private commonData: CommonDataService,
		private domainsService: DomainsService,
	) { }

	formatQueueFields(changes: { [key: string]: any}) {
		let changesToShow = [];

		changes.forEach(change => {
			for (const field in change) {
				let value;

				switch (true) {
					case this.booleanFields.includes(field):
						value = this.translate.instant(change[field] == 1 ? 'g.true' : 'g.false');
						break;
					case this.timeStampFields.includes(field):
						value = moment(change[field] * 1000).format('MM/DD/YYYY');
						break;
					case this.nbIotFields.includes(field):
						value = this.formatLteBands(field, change[field]);
						break;
					case this.customFields.includes(field) && field == 'cellular_region':
						value = this.ddLists.cellular_region.find(item => item.id == change[field])?.text;
						break;
					case this.customFields.includes(field) && field == 'sct_device_type':
						value = DeviceFactory.options.find(item => item.id == change[field])?.name;
						break;
					case this.customFields.includes(field) && field == 'battery_type':
						value = this.ddLists.battery_type.find(item => item.id == change[field])?.label;
						break;
					case this.customFields.includes(field) && field == 'metered_flag':
						value = this.ddLists.metered_flag.find(item => item.value == change[field])?.label;
						break;
					case this.customFields.includes(field) && field == 'zone_id':
						value = this.commonData.TimeZonesMenu.find(item => item.id == change[field])?.display_name;
						break;
					case this.networkingFields.includes(field):
						for (let modal in change[field]) {
							value = change[field][modal]
							if (['priority'].includes(modal))
								value = this.ddLists.networkPriorities.find(item => item.id == value).text;

							changesToShow.push({ 'key': [`${field}_${modal}`], 'value': value });
						}
						continue;
					default:
						value = change[field];
						break;
				}

				changesToShow.push({ 'key': field, 'value': value });
			}
		});

		return changesToShow;
	}

	formatLteBands(field: string, value: number) {
		const cellularField = field == 'lte_cellular_bands'? this.ddLists.lte_bands : this.ddLists.nbiot_bands;
		let cellularBandArray: number[] = [];
		for (const it of cellularField) {
			if ((value & it.value) != 0)
				cellularBandArray.push(it.value);
		}
		return cellularField.filter(obj => cellularBandArray.includes(obj.value)).map(obj => obj.text);
	}

	getChargerVoltageLabel(value: number | string): string | number {
		const option = this.ddLists.charger_voltage.find(item => item.value === Number(value));
		return option ? option.text : value;
	}

	getHallEffectSensorTypes(fwVersion: number, currentValue?: number) {
		return this.ddLists.hallEffectSensorTypes.filter(option => {
			// keep the device's stored sensor visible even when its firmware is below that option's minimum
			if (currentValue != null && option.id === currentValue)
				return true;

			return fwVersion >= option.minFwVersion;
		});
	}

	getApStatus(qvApStatus, qvApIp) {

		let AP_STATUS_STARTED = 1,
		AP_STATUS_STOPPED = 3,
		AP_STATUS_STA_CONNECTED = 4,
		AP_STATUS_STA_DISCONNECTED = 5,
		AP_STATUS_PROBEREQRECVED = 6,
		AP_STATUS_STA_GOT_IP6 = 7,
		AP_STATUS_MAX = 8;

		if(![AP_STATUS_STOPPED, AP_STATUS_MAX].includes(qvApStatus) && qvApIp != "0.0.0.0" && qvApIp)
			return this.translate.instant('qv.ap_on')+' '+qvApIp;

		return this.translate.instant('qv.ap_on');
	}

	getRemoteServerType(fwVersion: number, isIMDevice: boolean = false, sct_device_type: number) {
		if (isIMDevice)
			return this.getImRemoteServerType(fwVersion);

		const serversOptions = JSON.parse(JSON.stringify(this.ddLists.remoter_server_ip));

		const productionDomainMap: Record<number, string> = {
			0: this.domainsService.iotahProductionDomain,
			1: this.domainsService.charglinkProductionDomain,
			2: this.domainsService.battlinkProductionDomain,
			3: this.domainsService.brmProductionDomain
		};

		const selectedProductionDomain = productionDomainMap[sct_device_type];

		if (selectedProductionDomain) {
			const productionObj = serversOptions.find(obj => obj.id === 'production');
			if (productionObj) {
				productionObj.domain = selectedProductionDomain;
			}
		}
		if (fwVersion >= 2.46) {
			// use encCode 5 instead of 2 for production and staging
			serversOptions.forEach(obj => {
				if (obj.id == 'production' || obj.id == 'staging') {
					obj.encCode = 5
				}
			});
		}
		return serversOptions;
	}

	getImRemoteServerType(fwVersion: number) {
		const serversOptions = JSON.parse(JSON.stringify(this.ddLists.im_remoter_server_ip));

		if (fwVersion >= 1.2900) {
			// use encCode 5 instead of 2 for production and staging
			serversOptions.forEach(obj => {
				if (obj.id == 'production' || obj.id == 'staging')
					obj.encCode = 5;
				else
					obj.encCode = 3;
			});
		}
		return serversOptions;

	}
}
